import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store';
import {
  listProjects,
  openProject,
  saveProject,
  deleteProject,
  shareProject,
  openShared,
  CloudError,
  type CloudProjectMeta,
} from '../lib/cloud';
import { AccountMenu } from './AccountMenu';
import { IconPlus, IconTrash, IconSave, IconFolderOpen, IconFilePlus, IconClose } from './icons';

const CARD_ACCENTS = ['a-indigo', 'a-pink', 'a-cyan', 'a-amber', 'a-violet', 'a-green'];

function fmtSize(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Landing page for signed-in users: every saved project, ready to open. */
export function HomePage() {
  const user = useStore((s) => s.auth.user)!;
  const setView = useStore((s) => s.setView);
  const setProjectName = useStore((s) => s.setProjectName);
  const setPendingProject = useStore((s) => s.setPendingProject);
  const resetForNewProject = useStore((s) => s.resetForNewProject);
  const pushLine = useStore((s) => s.pushLine);

  const [projects, setProjects] = useState<CloudProjectMeta[] | null>(null);
  const [usage, setUsage] = useState<{ used: number; quota: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const list = await listProjects();
      setProjects(list.projects);
      setUsage({ used: list.usedBytes, quota: list.quotaBytes });
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not load your projects.');
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function openOntoCanvas(name: string, data: string): void {
    setProjectName(name);
    setPendingProject({ name, data });
    setView('studio');
  }

  function onOpenProject(id: string, name: string): void {
    void (async () => {
      try {
        const { project } = await openProject(id);
        const parsed = JSON.parse(project.data) as { workspace?: object };
        openOntoCanvas(project.name, JSON.stringify(parsed.workspace ?? parsed));
        pushLine('ok', `Opened "${name}".`);
      } catch (err) {
        setError(err instanceof CloudError ? err.message : `Could not open "${name}".`);
      }
    })();
  }

  function onNewProject(): void {
    resetForNewProject();
    setPendingProject({ name: 'untitled', data: null });
    setView('studio');
    pushLine('info', 'New project. The canvas is empty.');
  }

  function onImportFile(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as { workspace?: object };
        const name = file.name.replace(/\.spblock\.json$|\.json$/i, '') || 'imported';
        openOntoCanvas(name, JSON.stringify(parsed.workspace ?? parsed));
        pushLine('info', `Imported "${file.name}". Press Save to store it in the cloud.`);
      } catch {
        setError(`Could not read "${file.name}" — not a valid project file.`);
      }
    };
    reader.readAsText(file);
  }

  function onShare(id: string, name: string): void {
    void (async () => {
      try {
        const { shareCode } = await shareProject(id);
        try {
          await navigator.clipboard.writeText(shareCode);
          pushLine('ok', `"${name}" is shared — code ${shareCode} (copied to the clipboard).`);
        } catch {
          pushLine('ok', `"${name}" is shared — code ${shareCode}.`);
        }
      } catch (err) {
        setError(err instanceof CloudError ? err.message : 'Could not share the project.');
      }
    })();
  }

  function onDuplicate(id: string, name: string): void {
    void (async () => {
      setError(null);
      try {
        const { project } = await openProject(id);
        const copy = `${project.name} (copy)`;
        await saveProject(copy, project.data);
        pushLine('ok', `Duplicated "${name}" as "${copy}".`);
        void refresh();
      } catch (err) {
        setError(err instanceof CloudError ? err.message : 'Could not duplicate the project.');
      }
    })();
  }

  function onOpenShared(): void {
    setError(null);
    const code = shareCode.trim();
    if (!code) {
      setError('Enter a share code.');
      return;
    }
    void (async () => {
      setShareBusy(true);
      try {
        const { project } = await openShared(code);
        const parsed = JSON.parse(project.data) as { workspace?: object };
        openOntoCanvas(project.name, JSON.stringify(parsed.workspace ?? parsed));
        setShareOpen(false);
        setShareCode('');
        pushLine('ok', `Opened the shared project "${project.name}". Save it to keep your own copy.`);
      } catch (err) {
        setError(err instanceof CloudError ? err.message : 'Could not open the shared project.');
      } finally {
        setShareBusy(false);
      }
    })();
  }

  function onDelete(id: string, name: string): void {
    void (async () => {
      try {
        await deleteProject(id);
        pushLine('info', `Deleted "${name}".`);
        setConfirmId(null);
        void refresh();
      } catch (err) {
        setError(err instanceof CloudError ? err.message : 'Delete failed.');
      }
    })();
  }

  function onDownload(id: string, name: string): void {
    void (async () => {
      try {
        const { project } = await openProject(id);
        const blob = new Blob([project.data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project.name}.spblock.json`;
        a.click();
        URL.revokeObjectURL(url);
        pushLine('info', `Downloaded "${name}.spblock.json".`);
      } catch (err) {
        setError(err instanceof CloudError ? err.message : 'Download failed.');
      }
    })();
  }

  const quotaPct = usage && usage.quota > 0 ? Math.min(100, Math.round((usage.used / usage.quota) * 100)) : 0;

  return (
    <div className="home-page">
      <header className="header home-head">
        <span className="wordmark" onClick={() => setView('home')}>SPblock</span>
        <div className="header-actions">
          <button className="btn" onClick={onNewProject} title="Start a new empty project">
            <IconPlus />
            New project
          </button>
          <button className="btn" onClick={() => { setShareOpen(true); setError(null); }} title="Open a project that someone shared with you by its code">
            <IconFolderOpen />
            Open shared
          </button>
          <button className="btn" onClick={() => fileInput.current?.click()} title="Import a .spblock.json project file">
            <IconFilePlus />
            Import
          </button>
          <AccountMenu />
        </div>
      </header>

      <div className="home-body">
        <div className="home-hero">
          <div className="home-hero-inner">
            <h1>Your projects</h1>
            <p>
              {projects === null ? 'Loading…' : `${projects.length} project${projects.length === 1 ? '' : 's'} in your cloud storage`}
            </p>
            {usage && (
              <div className="quota-meter">
                <div className="quota-meter-bar">
                  <span style={{ width: `${quotaPct}%` }} />
                </div>
                <span className="quota-meter-text">
                  {fmtSize(usage.used)} of {Math.round((usage.quota / 1048576) * 10) / 10} MB used
                </span>
              </div>
            )}
          </div>
          <div className="home-hero-art" aria-hidden>
            <span className="chip chip-a" />
            <span className="chip chip-b" />
            <span className="chip chip-c" />
            <span className="chip chip-d" />
          </div>
        </div>

        {error && <div className="form-error err home-error">{error}</div>}

        <div className="project-grid">
          <button className="project-card new-card" onClick={onNewProject}>
            <span className="new-card-plus"><IconPlus width={26} height={26} /></span>
            <span className="new-card-title">New project</span>
            <span className="new-card-sub">Start from an empty canvas</span>
          </button>

          {projects?.map((p, i) => (
            <div key={p.id} className={`project-card ${CARD_ACCENTS[i % CARD_ACCENTS.length]}`}>
              <div className="card-accent" />
              <div className="card-body">
                <div className="card-title" title={p.name}>{p.name}</div>
                <div className="card-meta mono">
                  {fmtSize(p.sizeBytes)} · {fmtDate(p.updatedAt)}
                </div>
                <div className="card-actions">
                  <button className="btn btn-sm btn-primary" onClick={() => onOpenProject(p.id, p.name)}>
                    <IconFolderOpen width={13} height={13} />
                    Open
                  </button>
                  <button className="btn btn-sm" onClick={() => onDuplicate(p.id, p.name)} title="Create a copy of this project">
                    Duplicate
                  </button>
                  <button className="btn btn-sm" onClick={() => onShare(p.id, p.name)} title="Get a share code for this project">
                    Share
                  </button>
                  <button className="btn btn-sm" onClick={() => onDownload(p.id, p.name)} title="Download a project file">
                    <IconSave width={13} height={13} />
                  </button>
                  {confirmId === p.id ? (
                    <button className="btn btn-sm btn-danger" onClick={() => onDelete(p.id, p.name)}>
                      Confirm
                    </button>
                  ) : (
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirmId(p.id)} title="Delete project">
                      <IconTrash width={13} height={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="home-legal">
          <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
          {'\u00A0\u00B7\u00A0'}
          <a href="/terms.html" target="_blank" rel="noreferrer">Terms of Service</a>
        </div>

        {projects !== null && projects.length === 0 && !error && (
          <div className="home-empty">
            <IconFolderOpen width={30} height={30} />
            <p>No saved projects yet. Create your first one — the assistant can even build it for you.</p>
          </div>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onImportFile(file);
          e.target.value = '';
        }}
      />

      {shareOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Open shared project">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-head">
              <div className="modal-title">Open shared project</div>
              <button className="modal-x" aria-label="Close" onClick={() => setShareOpen(false)}>
                <IconClose width={14} height={14} />
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <div className="field-label"><span>Share code</span></div>
                <input
                  type="text"
                  value={shareCode}
                  placeholder="e.g. k7m2xw9p4q"
                  spellCheck={false}
                  autoFocus
                  onChange={(e) => setShareCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpenShared(); }}
                />
              </div>
              {error && <div className="form-error err">{error}</div>}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setShareOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={onOpenShared} disabled={shareBusy || !shareCode.trim()}>
                {shareBusy ? 'Opening…' : 'Open'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
